import * as React from 'react';
import { Menu as AriaMenu, MenuItem as AriaMenuItem } from 'react-aria-components';

import { ChevronDownIcon, LargeSearchIcon } from '../../../icons';
import { cx } from '../../../utils/cx';
import Button from '../../base/buttons/Button';
import { CheckboxBase } from '../../base/Checkbox/Checkbox';
import Dropdown from '../../base/Dropdown';
import Input from '../../base/Input';
import LoadingIndicator from '../../base/LoadingIndicator';
import { RadioButtonBase } from '../../base/RadioButtons/RadioButtons';

import type { Selection } from 'react-aria-components';

export interface IFilterOption {
  id: string;
  label: string;
  description?: React.ReactNode;
  avatar?: React.ReactNode;
  keywords?: string[];
  /** Optional key for custom sort order (e.g. "Bank Name|Account Name" for multi-level sort) */
  sortKey?: string;
}

export type SortCompareFn = (a: IFilterOption, b: IFilterOption) => number;

interface ITransactionFilterDropdownProps {
  label: string;
  options: IFilterOption[];
  selectedIds: string[];
  onSelect: (ids: string[]) => void;
  hideSearch?: boolean;
  hideSelectAll?: boolean;
  isLoading?: boolean;
  className?: string;
  /** Custom sort comparator. Defaults to sort by label, or by sortKey if present on options. */
  sortCompare?: SortCompareFn;
  /** Selection mode: 'single' allows one option, 'multiple' allows many. Defaults to 'multiple'. */
  selectionMode?: 'single' | 'multiple';
}

const defaultSortCompare: SortCompareFn = (a, b) => (a.sortKey ?? a.label).localeCompare(b.sortKey ?? b.label);

export const TransactionFilterDropdown = ({
  label,
  options,
  selectedIds,
  onSelect,
  isLoading = false,
  hideSearch = false,
  hideSelectAll = false,
  className,
  sortCompare = defaultSortCompare,
  selectionMode = 'multiple',
}: ITransactionFilterDropdownProps) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const [sortedOptions, setSortedOptions] = React.useState<IFilterOption[] | null>(null);
  const previousIsOpenRef = React.useRef(false);
  const isSelectingRef = React.useRef(false);

  React.useEffect(() => {
    const wasOpen = previousIsOpenRef.current;
    previousIsOpenRef.current = isOpen;

    if (!isOpen) {
      setSearchQuery('');
    } else if (!wasOpen && isOpen) {
      setSortedOptions(null);

      if (selectionMode === 'single') {
        setSortedOptions([...options].sort(sortCompare));
      } else {
        const selectedSet = new Set(selectedIds);
        const selected = options.filter((option) => selectedSet.has(option.id));
        const unselected = options.filter((option) => !selectedSet.has(option.id));

        const sortedSelected = [...selected].sort(sortCompare);
        const sortedUnselected = [...unselected].sort(sortCompare);

        setSortedOptions([...sortedSelected, ...sortedUnselected]);
      }
    }
  }, [isOpen, options, selectedIds, sortCompare, selectionMode]);

  const filteredOptions = React.useMemo(() => {
    // Use sorted options if available (when dropdown is open), otherwise use original options
    const optionsToFilter = sortedOptions ?? options;

    // Filter by search query if needed
    if (!hideSearch && searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return optionsToFilter.filter((option) => {
        return (
          option.keywords?.map((keyword) => keyword.toLowerCase()).some((keyword) => keyword.includes(query)) ?? false
        );
      });
    }

    return optionsToFilter;
  }, [options, searchQuery, hideSearch, sortedOptions]);

  const handleSelectionChange = React.useCallback(
    (keys: Selection) => {
      if (isLoading) {
        return;
      }

      isSelectingRef.current = true;
      requestAnimationFrame(() => {
        isSelectingRef.current = false;
      });

      if (keys === 'all' || keys === undefined) {
        onSelect([]);
        return;
      }
      const keySet = keys instanceof Set ? keys : new Set(keys);

      // If selection is empty, map to "all" (empty array)
      if (keySet.size === 0) {
        onSelect([]);
        return;
      }

      // Convert to string array
      const selected = Array.from(keySet).map((key) => String(key));
      onSelect(selected);
    },
    [isLoading, onSelect]
  );

  const handleOpenChange = React.useCallback((open: boolean) => {
    // Prevent the menu from closing when an item is selected
    if (!open && isSelectingRef.current) {
      return;
    }
    setIsOpen(open);
  }, []);

  const selectedKeys = React.useMemo<Selection>(() => {
    // Empty selection means "all"
    return new Set(selectedIds);
  }, [selectedIds]);

  const showBadge = selectedIds.length > 0;

  const button = (
    <Button
      size="sm"
      color="secondary"
      iconTrailing={<ChevronDownIcon className="size-4.5" />}
      aria-label={label}
      className="gap-2"
    >
      <div className="flex items-center gap-2">
        <span>{label}</span>
        {showBadge && (
          <div className="h-4.5 min-w-4.75 bg-primary-solid flex items-center justify-center rounded-full text-white">
            {selectedIds.length}
          </div>
        )}
      </div>
    </Button>
  );

  return (
    <Dropdown.Root isOpen={isOpen} onOpenChange={handleOpenChange}>
      {button}
      <Dropdown.Popover placement="bottom start" className="w-[370px] overflow-hidden">
        <div className={cx('max-h-100 h-100 flex flex-col overflow-hidden', className)}>
          {!hideSearch && (
            <div className="bg-secondary border-tertiary sticky top-0 z-10 flex flex-col gap-2 border-b px-5 py-4">
              <Input
                icon={LargeSearchIcon}
                placeholder={`Search ${label.toLowerCase()}`}
                value={searchQuery}
                onChange={setSearchQuery}
                size="md"
                wrapperClassName="w-full rounded-full shadow-none"
                inputClassName="h-9 rounded-full"
              />
            </div>
          )}
          {!isLoading && (
            <div className="border-tertiary bg-primary flex shrink-0 items-center justify-end gap-4 border-t px-5 py-1.5">
              {selectionMode === 'multiple' && !hideSelectAll && (
                <Button
                  size="xs"
                  color="link-gray"
                  onClick={() => onSelect([...new Set([...selectedIds, ...filteredOptions.map((o) => o.id)])])}
                >
                  Select all
                </Button>
              )}
              <Button size="xs" color="link-gray" onClick={() => onSelect([])}>
                Clear
              </Button>
            </div>
          )}
          <AriaMenu
            selectionMode={selectionMode}
            disallowEmptySelection={false}
            selectedKeys={selectedKeys}
            onSelectionChange={handleSelectionChange}
            className="outline-hidden flex-1 select-none overflow-y-auto"
          >
            {isLoading ? (
              <AriaMenuItem className="outline-hidden flex min-h-[200px] items-center justify-center py-16">
                <LoadingIndicator size="md" />
              </AriaMenuItem>
            ) : (
              filteredOptions.map((option) => (
                <AriaMenuItem
                  key={option.id}
                  id={option.id}
                  className={(state) =>
                    cx('outline-hidden group block cursor-pointer', state.isDisabled && 'cursor-not-allowed')
                  }
                >
                  {(state) => (
                    <div
                      className={cx(
                        'outline-focus-ring border-tertiary bg-primary relative flex h-16 items-center justify-between border-b px-5 transition duration-100 ease-linear',
                        !state.isDisabled && 'group-hover:bg-primary_hover',
                        state.isFocused && 'bg-primary_hover',
                        state.isFocusVisible && 'outline-2 -outline-offset-2'
                      )}
                    >
                      <div className="flex min-w-0 grow items-center gap-2">
                        {option.avatar}
                        <div className="flex min-w-0 flex-col">
                          <div className="text-md truncate font-medium">{option.label}</div>
                          {option.description && (
                            <div className="text-tertiary truncate text-sm">{option.description}</div>
                          )}
                        </div>
                      </div>
                      {selectionMode === 'single' ? (
                        <RadioButtonBase size="sm" isSelected={selectedIds.includes(option.id)} className="shrink-0" />
                      ) : (
                        <CheckboxBase size="sm" isSelected={selectedIds.includes(option.id)} className="shrink-0" />
                      )}
                    </div>
                  )}
                </AriaMenuItem>
              ))
            )}
          </AriaMenu>
        </div>
      </Dropdown.Popover>
    </Dropdown.Root>
  );
};
